import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { tCell, Button, GenericTable, Dropdown, DateTimePicker, Dialog } from "../utils";
import DynamicFilter from "../utils/DynamicFilter";
import { useOrgs, usePlant, useThirdPartyLogs, useUserDetail, useUserPermissions, useUserTableHeader } from "../../services/hooks";
import { usePlantContext } from "../../context/plantContext";
import { findDeepValue } from "../../services/functions";
import { buttonClasses } from "../utils/theme";
import { formatDate } from "../../services/functions";
import { Navigate } from "react-router-dom/dist";
import dayjs from "dayjs";
import "./ThirdPartyLogs.css";

const defaultFilter = {
	pagesize: 10,
	page_no: 1,
};

const today = dayjs();
const startDate = today.subtract(1, "day");
const endDate = today;

const ThirdPartyLogs = () => {
	const location = useLocation();
	const { userDetail, isSuperAdmin } = useUserDetail();
	const { userPermissions, isFetched } = useUserPermissions();
	const { plantId: contextPlantId } = usePlantContext();
	const filterRef = useRef(defaultFilter);
	const { page_no, pagesize } = filterRef.current;
	const { logs, count, tableHeader, isLoading, getThirdPartyLogs } = useThirdPartyLogs();
	const { userTableHeaders, getUserTableHeaders } = useUserTableHeader();
	const dynamicFilterRef = useRef(null);
	const initialCallDone = useRef(false);

	// 1. Fetch table header config on mount (includes filterConfig for dynamic filters)
	useEffect(() => {
		getUserTableHeaders({ template_id: "default-apilogs-listing" });
	}, []);

	// userTableHeaders is the attributes array from Builder Service (fetched once on mount)
	const filterConfigAttributes = Array.isArray(userTableHeaders) ? userTableHeaders : [];

	// --- Inline filter state (org, plant, date range) ---
	const [filter, setFilter] = useState({
		startDate,
		endDate,
		orgId: isSuperAdmin ? "" : userDetail?.orgId || "",
		orgName: isSuperAdmin ? "" : userDetail?.orgName || "",
		plantId: !isSuperAdmin && contextPlantId ? contextPlantId : "",
	});

	const handleFilterChange = (name, value) => {
		setFilter((o) => ({ ...o, [name]: value }));
	};

	useEffect(() => {
		if (!isSuperAdmin && userDetail?.orgId) {
			setFilter((o) => ({ ...o, orgId: userDetail.orgId, orgName: userDetail.orgName || "", plantId: contextPlantId || "" }));
		}
	}, [isSuperAdmin, userDetail, contextPlantId]);

	useEffect(() => {
		if (!location.state) return;
		const { orgId, orgName } = location.state;
		setFilter((o) => ({ ...o, orgId: orgId || o.orgId, orgName: orgName || o.orgName }));
	}, [location]);

	const { orgOptions } = useOrgs({}, { skip: !isSuperAdmin });
	const orgValue = orgOptions.find((_) => _.value == filter.orgId) || null;

	const { plantOptions, getPlantDetails, clearPlants } = usePlant();
	const plantValue = plantOptions.find((_) => _.value == filter.plantId) || null;

	useEffect(() => {
		if (filter.orgId) {
			getPlantDetails({ orgId: filter.orgId });
		} else {
			clearPlants();
		}
	}, [filter.orgId]);

	// 2 & 3. Extract default filters from filterConfig
	function getDefaultFilters(attributes) {
		const defaults = [];
		attributes.forEach((attr) => {
			const cfg = attr.filterConfig;
			if (cfg?.value && attr.isSearchable) {
				defaults.push({ path: attr.key || cfg._id, value: cfg.value });
			}
		});
		return defaults;
	}

	// --- Single Submit: collects inline + dynamic filters ---
	function handleSubmit() {
		const filters = [];
		if (filter.plantId) {
			filters.push({ path: "plantId", value: filter.plantId });
		}

		// Collect dynamic filters from DynamicFilter ref,
		// fallback to default values from filterConfig if ref not ready yet
		const dynamicFilters = dynamicFilterRef.current?.getFilters();
		filters.push(...(dynamicFilters?.length ? dynamicFilters : getDefaultFilters(filterConfigAttributes)));

		filterRef.current = {
			...filterRef.current,
			startDate: filter.startDate?.$d == "Invalid Date" ? new Date().toISOString() : filter.startDate.toISOString(),
			endDate: filter.endDate?.$d == "Invalid Date" ? new Date().toISOString() : filter.endDate.toISOString(),
			orgId: filter.orgId,
			filters,
			page_no: 1,
		};
		getThirdPartyLogs(filterRef.current);
	}

	// 4. Auto-fetch: wait for filterConfigAttributes to be ready, then call third-party-logs
	useEffect(() => {
		if (!filter.orgId || initialCallDone.current) return;
		if (!filterConfigAttributes.length) return; // wait until getUserTableHeaders returns
		initialCallDone.current = true;
		handleSubmit();
	}, [filter.orgId, filterConfigAttributes.length]);

	// --- Table rendering (uses tableHeader from listing API for columns) ---
	const hasDynamicFilters = filterConfigAttributes.some((attr) => attr.filterConfig);

	const [showJsonView, setShowJsonView] = useState(false);
	const jsonData = useRef({});

	let header = ["S.No", ...(tableHeader || []).map((_) => _.label)];

	let rows = logs?.map((data, i) => {
		let row = [tCell((page_no - 1) * pagesize + i + 1)];

		for (let item of tableHeader || []) {
			let value = findDeepValue(data, item.name.split(".")) ?? "";

			if (value && item.type === "object") {
				row.push(
					tCell(
						`${Object.keys(value).length} Fields`,
						"var(--clr-primary)",
						"pointer",
						() => {
							setShowJsonView(true);
							jsonData.current = value;
						},
						i
					)
				);
			} else {
				row.push(tCell(item.type === "date" ? formatDate(value, item.format) : (value !== "" ? String(value) : " - ")));
			}
		}

		return row;
	});

	const onPageChange = (page_no) => {
		filterRef.current = { ...filterRef.current, page_no: page_no + 1 };
		getThirdPartyLogs(filterRef.current);
	};
	const onPageSizeChange = (e) => {
		const { value } = e.target;
		filterRef.current = { ...filterRef.current, pagesize: value, page_no: 1 };
		getThirdPartyLogs(filterRef.current);
	};

	if (isFetched && !isSuperAdmin && !userPermissions?.viewApiLogs?.value) return <Navigate to="/not-authorized" replace />;

	return (
		<div className="contentpanel">
			<div className="cust-row flex-algn-cent">
				<div className="cust-col-5">
					<h1 className="title">API Logs</h1>
				</div>
			</div>
			<hr className="bgg" />

			{/* All filters in one grid — inline + dynamic + single Submit */}
			<div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "1rem", marginBottom: "1rem" }}>
				{isSuperAdmin && (
					<Dropdown
						label="Organization"
						name="orgId"
						value={orgValue}
						options={orgOptions}
						onChange={(op) => {
							handleFilterChange("orgId", op?.value || "");
							handleFilterChange("orgName", op?.label || "");
							handleFilterChange("plantId", "");
						}}
					/>
				)}
				{filter.orgId && (
					<Dropdown
						label="Plant"
						name="plantId"
						value={plantValue}
						options={plantOptions}
						onChange={(op) => {
							handleFilterChange("plantId", op?.value || "");
						}}
					/>
				)}
				<DateTimePicker
					label="From"
					value={filter.startDate}
					onChange={(value) => handleFilterChange("startDate", value)}
				/>
				<DateTimePicker
					label="To"
					value={filter.endDate}
					onChange={(value) => handleFilterChange("endDate", value)}
				/>

				{/* Dynamic filters from getUserTableHeaders filterConfig — stable, won't reset */}
				{hasDynamicFilters && (
					<DynamicFilter ref={dynamicFilterRef} attributes={filterConfigAttributes} />
				)}

				<Button text="Submit" onClick={handleSubmit} style={{ ...buttonClasses.lynkitOrangeFill, width: "fit-content", height: "fit-content" }} disabled={isLoading} />
			</div>

			<div className="main_content">
				<GenericTable header={header} rows={rows} pageCount={count} pageNo={page_no} limit={pagesize} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange} isLoading={isLoading} />
			</div>

			{showJsonView ? (
				<Dialog title="JSON Viewer" size="xl" open={showJsonView} handleClose={() => setShowJsonView(false)} noBtn>
					{parseJson(jsonData.current, true)}
				</Dialog>
			) : null}
		</div>
	);
};

export default ThirdPartyLogs;

function parseJson(obj, collapsible) {
	let ui = [];
	for (let item in obj) {
		ui.push(handleJsonItem(item, obj[item], collapsible));
	}
	return <div className="json">{ui}</div>;
}

function handleJsonItem(key, value, collapsible) {
	let type = Array.isArray(value) ? "array" : typeof value;

	if (typeof value === "object") {
		let html = [];
		for (let item in value) {
			html.push(handleJsonItem(item, value[item], collapsible));
		}
		return JsonItemCollapsible(key, type, type, collapsible, html);
	}
	return JsonItem(key, type == "string" ? `"${value}"` : value, type);
}

function JsonItem(key, value, type) {
	return (
		<div className="json__item" key={key + value + type}>
			<div className="json__key">{key}</div>
			<div className={`json__value json__value--${type}`}>{value}</div>
		</div>
	);
}

function JsonItemCollapsible(key, value, type, collapsible = false, children) {
	return (
		<label className="json__item json__item--collapsible">
			<input type="checkbox" className="json__toggle" defaultChecked={collapsible} />
			<div className="json__key">{key}</div>
			<div className={`json__value json__value--${type}`}>{value}</div>
			{children}
		</label>
	);
}
